import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { CsPeriodToggle, type CsPeriod } from '@/components/ui/PeriodToggle';
import { TrendChart, type TrendPoint } from '@/components/charts/TrendChart';
import { kpiDef, type KpiId } from '@/lib/kpi-definitions';
import { aggregationLabel, latestPeriodLabel, latestValue } from '@/lib/aggregate';
import type { CsMetrics } from '@/lib/api';

interface CsQualitySectionProps {
  cs: CsMetrics | null;
  /** 選択期間で集約済みのトレンド（古い順） */
  csatTrend: TrendPoint[];
  firstResponseTrend: TrendPoint[];
  answerSlaTrend: TrendPoint[];
  threeTurnTrend: TrendPoint[];
  resolutionTrend: TrendPoint[];
  aiCompletionTrend: TrendPoint[];
  /** X軸ラベル（'期間（月次）' 等） */
  xLabel: string;
  /** このセクションの集計期間（週次/月次/四半期） */
  csPeriod: CsPeriod;
  onCsPeriodChange: (period: CsPeriod) => void;
  isLoading?: boolean;
}

/** @graph-connects none */
function pct(value: number | null, digits = 1): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

interface SlaTileProps {
  /** KPI定義レジストリの識別子。ラベル・ゴール・今期の運用目標はここから引く */
  id: KpiId;
  /** 選択期間で集約済みのトレンド（古い順）。**表示する数字もここから取る** */
  trend: TrendPoint[];
  xLabel: string;
  /** 集約方法ラベルは**指標ごとに引く**。共通で渡すと週次SLOに「週次平均」と出てしまう */
  csPeriod: CsPeriod;
}

/**
 * ⚠️ **数字を `value` prop で外から渡さない。** 2026-10-08 まで `/kpi` スナップショット
 * （＝常に直近の確定「月」）を渡していたため、期間トグルを週次・四半期に切り替えても
 * 数字だけ動かず、グラフとカードが別の期間を指していた。
 * **描いている系列の末尾を出す**ことで、構造的にずれなくなる。
 *
 * @graph-connects none
 */
function SlaTile({ id, trend, xLabel, csPeriod }: SlaTileProps) {
  const { label, metricName, goal, target } = kpiDef(id);
  const value = latestValue(trend);
  // 数字と同じ点から取る。CS は4本とも同じ月を指すが、CRM と表記を揃えておく
  const periodLabel = latestPeriodLabel(trend);
  // 待ち時間SLO2本の週次は API がその週の率をそのまま返すので「平均」ではない。
  // セクション共通のラベルを配ると誤表示になるため、指標ごとに引く
  const aggregation = aggregationLabel(id, csPeriod);
  const achieved = value != null && target != null && value >= target;
  // 2026-10-06: **未達バッジは出さない。** 今期SLOが4本とも100%で、初動は構造的に到達不能。
  // 常時赤が4枚並ぶだけで情報量が無く、さらに CS系は未配線で値が null のため
  // **データが無いのに「未達」と出ていた**（データ欠損と未達は別物）。
  // 達成したものだけバッジで示し、足りない分は「（あと N pt）」の残り pt で読む
  // （docs の「未達かどうかではなく、ゴールまでの残り pt の縮み方を見る」に合わせた）。
  const gapTo = (basis: number | null): string | null => {
    if (value == null || basis == null || value >= basis) return null;
    return `（あと ${((basis - value) * 100).toFixed(1)}pt）`;
  };
  return (
    <GlassCard padding="none" className="overflow-hidden">
      <div className="p-5">
        {/* タイトルの行数はカードごとに違うため、items-start で各カードのバッジの高さを揃える */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              {label}
            </p>
            {periodLabel != null && (
              <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                {periodLabel}
              </span>
            )}
          </div>
          {achieved && (
            <Badge variant="success" size="sm">
              達成
            </Badge>
          )}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span
            className="text-3xl font-bold tabular-nums"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {pct(value)}
          </span>
          {target != null ? (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              目標 {pct(target, 0)}
              {gapTo(target)}
            </span>
          ) : (
            goal != null && (
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                ゴール {pct(goal, 0)}
                {gapTo(goal)}
              </span>
            )
          )}
        </div>
      </div>
      {trend.length > 0 && (
        <div className="px-1 pb-1">
          <TrendChart
            data={trend}
            yLabel={metricName}
            xLabel={xLabel}
            aggregation={aggregation}
            unit="percent"
            // 達成したときだけ緑。それ以外は中立色で塗る（2026-10-06）。バッジを消しても
            // 線が警告色のままだとカード全体が「赤」に見え、未達バッジと同じノイズになる
            color={achieved ? 'var(--color-success)' : 'var(--color-primary)'}
            height={176}
          />
        </div>
      )}
    </GlassCard>
  );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business チームCS機能の対応品質セクション。状態ゴールを分解したKPI4本（初動SLO・回答SLO・3ターム解決率・解決率）と、手段・ガードレールのモニタリング2本（AI完遂率・満足度）を層に分けて可視化する。CS単体の看板数字（KGI指標）は持たない
 * @graph-connects TrendChart [calls] 各KPIの推移を軸付きで描画
 */
export function CsQualitySection({
  cs,
  csatTrend,
  firstResponseTrend,
  answerSlaTrend,
  threeTurnTrend,
  resolutionTrend,
  aiCompletionTrend,
  xLabel,
  csPeriod,
  onCsPeriodChange,
  isLoading,
}: CsQualitySectionProps) {
  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="skeleton-shimmer h-3 w-32 rounded mb-3" />
        {/* KPI4枚 + モニタリング2枚 = 6枚。実体のタイル数と揃えないと描画時に行数が飛ぶ */}
        <div className="bento-grid">
          <div className="glass-card p-5 skeleton-shimmer h-32" />
          <div className="glass-card p-5 skeleton-shimmer h-32" />
          <div className="glass-card p-5 skeleton-shimmer h-32" />
          <div className="glass-card p-5 skeleton-shimmer h-32" />
          <div className="glass-card p-5 skeleton-shimmer h-32" />
          <div className="glass-card p-5 skeleton-shimmer h-32" />
        </div>
      </div>
    );
  }

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            CS 対応品質
          </h2>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {kpiDef('firstResponseSlaRate').metricName} / {kpiDef('answerSlaRate').metricName} /{' '}
            {kpiDef('threeTurnRate').metricName} / {kpiDef('resolutionRate').metricName}{' '}
            の4本で状態ゴールを表す
          </span>
          {cs?.inquiryCount != null && (
            <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
              ・問い合わせ {cs.inquiryCount.toLocaleString()} 件/日
            </span>
          )}
        </div>
        {/* 日次データを持つのは CS 系の指標だけなので、週次まで刻めるトグルはここにだけ置く */}
        <CsPeriodToggle value={csPeriod} onChange={onCsPeriodChange} />
      </div>

      {/*
        2026-09-29: CS単体の看板数字（KGI指標）は置かない。状態ゴールは文で表し、
        数字はKPI4本で示す。4本は状態ゴール文の分解なので、全て100%ならゴールは満たされる。
      */}
      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
        KPI
      </p>
      <div className="bento-grid mb-5">
        <SlaTile
          id="firstResponseSlaRate"
          trend={firstResponseTrend}
          xLabel={xLabel}
          csPeriod={csPeriod}
        />
        <SlaTile id="answerSlaRate" trend={answerSlaTrend} xLabel={xLabel} csPeriod={csPeriod} />
        <SlaTile id="threeTurnRate" trend={threeTurnTrend} xLabel={xLabel} csPeriod={csPeriod} />
        <SlaTile id="resolutionRate" trend={resolutionTrend} xLabel={xLabel} csPeriod={csPeriod} />
      </div>

      {/*
        モニタリングは「手段」と「ガードレール」。AI完遂率は状態ゴールのどの節にも対応せず、
        上げること自体を目的化すると解決率を壊すため目標値を置かない。満足度は測定母集団が
        全体の10.8%しかないので、主たる判定は解決率に任せて補助信号として見る。
      */}
      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
        モニタリング
      </p>
      <div className="bento-grid">
        <SlaTile
          id="aiCompletionRate"
          trend={aiCompletionTrend}
          xLabel={xLabel}
          csPeriod={csPeriod}
        />
        <SlaTile id="csat" trend={csatTrend} xLabel={xLabel} csPeriod={csPeriod} />
      </div>
    </section>
  );
}
